import { Award, Trophy, Crown, Sparkles } from "lucide-react";

const TIER_STYLES = {
  gold: {
    bg: "bg-gradient-to-br from-[#F5C349] via-[#D8A02D] to-[#8E6510]",
    ring: "ring-[#E7B24A]",
    icon: Crown,
    label: "Ouro",
    caption: "Referenciador de elite",
  },
  silver: {
    bg: "bg-gradient-to-br from-[#DADADA] via-[#B4B4B4] to-[#7A7A7A]",
    ring: "ring-[#C0C0C0]",
    icon: Trophy,
    label: "Prata",
    caption: "Referenciador consistente",
  },
  bronze: {
    bg: "bg-gradient-to-br from-[#D89966] via-[#B57238] to-[#7A4A20]",
    ring: "ring-[#CD7F32]",
    icon: Award,
    label: "Bronze",
    caption: "Bem-vindo ao clube",
  },
  none: {
    bg: "bg-gradient-to-br from-[#E9E7E1] via-[#D5D2CB] to-[#B7B4AB]",
    ring: "ring-[var(--border)]",
    icon: Sparkles,
    label: "Sem emblema",
    caption: "A sua primeira contratação está a caminho",
  },
};

export function tierStyle(key) {
  return TIER_STYLES[key || "none"];
}

/** Compact chip variant (nav / cards) */
export function TierChip({ tier, className = "", testId = "tier-chip" }) {
  if (!tier) return null;
  const s = tierStyle(tier.key);
  const Icon = s.icon;
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold text-white ${s.bg} ${className}`}
    >
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

/** Large badge card for dashboard */
export default function TierBadge({ tier, className = "", testId = "tier-badge" }) {
  if (!tier) return null;
  const s = tierStyle(tier.key);
  const Icon = s.icon;
  const nextAt = tier.next_at;
  const remaining = nextAt ? Math.max(nextAt - tier.hires, 0) : 0;
  const progressPct = Math.round((tier.progress ?? 0) * 100);

  return (
    <div
      data-testid={testId}
      className={`card-surface p-6 relative overflow-hidden ${className}`}
    >
      <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full opacity-30 blur-2xl pointer-events-none" style={{ background: "currentColor" }} aria-hidden />
      <div className="flex items-start gap-4">
        <div className={`w-14 h-14 rounded-2xl grid place-items-center text-white ring-4 ring-offset-2 ring-offset-white ${s.bg} ${s.ring}`}>
          <Icon className="w-7 h-7" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="eyebrow">Distintivo</div>
          <div className="font-heading text-3xl mt-1 text-[var(--ink)]">{s.label}</div>
          <div className="text-xs text-[var(--ink-soft)] mt-1">{s.caption}</div>
        </div>
      </div>

      {nextAt ? (
        <div className="mt-5">
          <div className="flex items-center justify-between text-xs text-[var(--ink-soft)]">
            <span>{tier.hires} contratação{tier.hires === 1 ? "" : "ões"}</span>
            <span>
              {remaining === 0
                ? "Objetivo alcançado!"
                : `Faltam ${remaining} para ${remaining === 1 ? "subir" : "atingir o próximo nível"}`}
            </span>
          </div>
          <div className="mt-2 h-2 rounded-full bg-[var(--muted)] overflow-hidden">
            <div
              className={`h-full ${s.bg} transition-all duration-500`}
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="mt-2 flex items-center gap-2 text-[11px] text-[var(--ink-soft)]">
            <span>Próximo:</span>
            <span className="chip chip-neutral">{nextAt === 3 ? "Prata · 3 contratações" : "Ouro · 10 contratações"}</span>
          </div>
        </div>
      ) : (
        <div className="mt-5 p-3 rounded-xl bg-[rgba(217,108,74,0.08)] border border-[rgba(217,108,74,0.2)] text-xs text-[var(--ink-soft)]">
          Está no topo. Obrigado por ser um <strong className="text-[var(--terracotta)]">Referenciador de Ouro</strong>.
        </div>
      )}
    </div>
  );
}
